/**
 * FiltersBar — shared filter controls for report pages.
 *
 * Props:
 *   value:    current filter state object (whatever keys make sense per page)
 *   onChange: (nextState) => void
 *   show:     array of keys to render, e.g. ['agent','queue','direction','cluster']
 *   options:  data from reportsApi.filterOptions() — { users, queues, dids, clusters, forms, dispositions, directions }
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../../api/client';

const LABELS = {
  agent:       'Agent',
  queue:       'Queue',
  direction:   'Direction',
  cluster:     'DC',
  did:         'DID',
  disposition: 'Disposition',
  form_id:     'QA Form',
  evaluator:   'Evaluator',
};

export default function FiltersBar({ value = {}, onChange, show = [], options }) {
  // If options wasn't passed, fetch them ourselves.
  const { data: fetched, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['reports','filters','options'],
    queryFn:  () => reportsApi.filterOptions(),
    enabled:  !options,
    staleTime: 60_000,
    retry: 1,
  });
  const opts = options || fetched || {};
  // Only show the error banner when we owned the fetch (i.e., options prop wasn't passed).
  const showLoadError = !options && isError;
  const errMsg = error?.response?.data?.detail || error?.message || 'Unknown error';
  const set = (k, v) => {
    const next = { ...value };
    if (v === '' || v === null || v === undefined) delete next[k];
    else next[k] = v;
    onChange(next);
  };
  const has = (k) => show.includes(k);
  const anySet = Object.values(value).some(v => v !== '' && v != null);

  return (
    <div className="mb-3">
      {showLoadError && (
        <div
          role="alert"
          className="mb-2 flex items-center gap-2 rounded-thin border border-amber-400/60 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 text-xs text-amber-900 dark:text-amber-200"
        >
          <span aria-hidden="true">⚠️</span>
          <span className="flex-1">
            Couldn&rsquo;t load filter options &mdash; dropdowns will be empty.
            <span className="opacity-70 ml-1">({errMsg})</span>
          </span>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="px-2 py-0.5 rounded-thin border border-amber-500/60 hover:bg-amber-100 dark:hover:bg-amber-800/40 disabled:opacity-50"
          >
            {isFetching ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center text-xs">
      {has('agent') && (
        <select value={value.agent || ''} onChange={(e) => set('agent', e.target.value)}
          className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base">
          <option value="">All agents</option>
          {(opts.users || []).map(u => (
            <option key={u.extension} value={u.extension}>{u.extension} · {u.name}</option>
          ))}
        </select>
      )}
      {has('queue') && (
        <select value={value.queue_id || ''} onChange={(e) => set('queue_id', e.target.value)}
          className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base">
          <option value="">All queues</option>
          {(opts.queues || []).map(q => (
            <option key={q.queue_id} value={q.queue_id}>{q.queue_id} · {q.name}</option>
          ))}
        </select>
      )}
      {has('direction') && (
        <select value={value.direction || ''} onChange={(e) => set('direction', e.target.value)}
          className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base">
          <option value="">All directions</option>
          {(opts.directions || ['inbound','outbound','internal']).map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      )}
      {has('cluster') && (
        <select value={value.cluster || ''} onChange={(e) => set('cluster', e.target.value)}
          className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base">
          <option value="">Both DCs</option>
          {(opts.clusters || []).map(c => (
            <option key={c.name} value={c.name}>{c.display_name || c.name} ({c.state})</option>
          ))}
        </select>
      )}
      {has('did') && (
        <select value={value.did || ''} onChange={(e) => set('did', e.target.value)}
          className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base">
          <option value="">All DIDs</option>
          {(opts.dids || []).map(d => (
            <option key={d.did} value={d.did}>{d.did}{d.description ? ' · ' + d.description : ''}</option>
          ))}
        </select>
      )}
      {has('disposition') && (
        <select value={value.disposition || ''} onChange={(e) => set('disposition', e.target.value)}
          className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base">
          <option value="">Any disposition</option>
          {(opts.dispositions || ['ANSWERED','NO ANSWER','BUSY','FAILED','CONGESTION']).map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      )}
      {has('form_id') && (
        <select value={value.form_id || ''} onChange={(e) => set('form_id', e.target.value)}
          className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base">
          <option value="">All forms</option>
          {(opts.forms || []).map(f => (
            <option key={f.form_id} value={f.form_id}>{f.name}</option>
          ))}
        </select>
      )}
      {has('evaluator') && (
        <select value={value.evaluator || ''} onChange={(e) => set('evaluator', e.target.value)}
          className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base">
          <option value="">All evaluators</option>
          {(opts.users || []).map(u => (
            <option key={u.user_id} value={u.user_id}>{u.name}</option>
          ))}
        </select>
      )}
      {anySet && (
        <button onClick={() => onChange({})}
          className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle">
          Clear filters
        </button>
      )}
      </div>
    </div>
  );
}
